import { Link, useNavigate } from 'react-router-dom';
import { User, Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from './ui/Button';
import { useAuth } from './AuthProvider';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import SportFundLogo from './SportFundLogo';
import { cn } from '@/lib/utils';

interface HomeHeaderProps {
    hideAuthLinks?: boolean;
    transparent?: boolean;
}

export default function HomeHeader({ hideAuthLinks = false, transparent = false }: HomeHeaderProps) {
    const { user, userRoles, signOut } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Transparent over hero → frosted-glass + dark text after scrolling 40px.
    useEffect(() => {
        if (!transparent) return;
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [transparent]);

    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handleDashboardClick = () => {
        if (userRoles?.creator) {
            navigate('/creator');
        } else {
            navigate('/dashboard');
        }
    };

    const isOverHero = transparent && !scrolled;
    const navLinkClass = isOverHero
        ? 'text-white/85 hover:text-white transition-colors font-medium'
        : 'text-slate-700 hover:text-slate-900 transition-colors font-medium';

    return (
        <header
            className={cn(
                'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
                transparent
                    ? scrolled
                        ? 'bg-white/85 backdrop-blur-md border-b border-slate-200/70 shadow-sm'
                        : 'bg-transparent'
                    : 'bg-white border-b border-slate-200/70'
            )}
        >
            <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" aria-label="Global">
                <div className="flex items-center">
                    <Link to="/" className="-m-1.5 p-1.5 flex items-center" aria-label="Sport Fund — home">
                        <SportFundLogo className={cn('h-9 md:h-10 w-auto', isOverHero ? 'text-white' : 'text-slate-900')} />
                    </Link>
                </div>

                {/* Desktop nav */}
                <div className="hidden md:flex items-center gap-8">
                    <Link to="/about" className={navLinkClass}>{t('nav.about')}</Link>
                    <Link to="/how-it-works" className={navLinkClass}>{t('nav.howItWorks')}</Link>
                    <Link to="/faqs" className={navLinkClass}>{t('nav.pricing')}</Link>
                    <Link to="/resources" className={navLinkClass}>{t('nav.resources')}</Link>
                </div>

                {/* Desktop auth/actions */}
                <div className="hidden md:flex items-center gap-3">
                    <LanguageSelector
                        triggerClassName={cn(
                            'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium bg-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-primary transition-colors',
                            isOverHero
                                ? 'text-white hover:bg-white/10'
                                : 'text-slate-700 hover:bg-slate-100'
                        )}
                    />
                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger
                                className={cn(
                                    'flex items-center justify-center w-10 h-10 rounded-full transition-colors',
                                    isOverHero
                                        ? 'bg-white/15 hover:bg-white/25 text-white'
                                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                                )}
                            >
                                <User className="w-5 h-5" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={handleDashboardClick}>
                                    {t('nav.dashboard')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleSignOut}>
                                    {t('actions.logout')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : hideAuthLinks ? null : (
                        <>
                            <Link to="/signin">
                                <Button
                                    variant="ghost"
                                    className={cn(
                                        'rounded-full font-medium',
                                        isOverHero
                                            ? 'text-white hover:bg-white/10'
                                            : 'text-slate-700 hover:bg-slate-100'
                                    )}
                                >
                                    {t('actions.login')}
                                </Button>
                            </Link>
                            <Link to="/signup-creator">
                                <Button
                                    className={cn(
                                        'rounded-full font-semibold px-5 transition-all',
                                        isOverHero
                                            ? 'bg-yellow-400 hover:bg-yellow-300 text-slate-900 shadow-lg shadow-yellow-400/30'
                                            : 'bg-blue-900 hover:bg-blue-800 text-white shadow-md shadow-blue-900/20'
                                    )}
                                >
                                    {t('actions.getStarted')}
                                </Button>
                            </Link>
                        </>
                    )}
                </div>

                {/* Mobile hamburger */}
                <button
                    type="button"
                    className={cn(
                        'md:hidden inline-flex items-center justify-center rounded-md p-2',
                        isOverHero ? 'text-white' : 'text-slate-900'
                    )}
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label="Toggle menu"
                >
                    {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
            </nav>

            {/* Mobile menu */}
            {isMobileMenuOpen && (
                <div className="md:hidden border-t border-slate-200 bg-white">
                    <div className="px-6 py-4 space-y-3">
                        <Link to="/about" onClick={closeMobileMenu} className="block w-full text-left text-slate-900 font-medium py-2">
                            {t('nav.about')}
                        </Link>
                        <Link to="/how-it-works" onClick={closeMobileMenu} className="block w-full text-left text-slate-900 font-medium py-2">
                            {t('nav.howItWorks')}
                        </Link>
                        <Link to="/faqs" onClick={closeMobileMenu} className="block w-full text-left text-slate-900 font-medium py-2">
                            {t('nav.pricing')}
                        </Link>
                        <Link to="/resources" onClick={closeMobileMenu} className="block w-full text-left text-slate-900 font-medium py-2">
                            {t('nav.resources')}
                        </Link>
                        <div className="pt-2 border-t border-slate-100">
                            <LanguageSelector />
                        </div>
                        {user ? (
                            <div className="pt-2 space-y-2">
                                <Button variant="ghost" className="w-full justify-start text-slate-900"
                                    onClick={() => { closeMobileMenu(); handleDashboardClick(); }}>
                                    {t('nav.dashboard')}
                                </Button>
                                <Button variant="ghost" className="w-full justify-start text-slate-900"
                                    onClick={() => { closeMobileMenu(); handleSignOut(); }}>
                                    {t('actions.logout')}
                                </Button>
                            </div>
                        ) : hideAuthLinks ? null : (
                            <div className="pt-2 space-y-2">
                                <Link to="/signin" onClick={closeMobileMenu} className="block">
                                    <Button variant="ghost" className="w-full text-slate-900">{t('actions.login')}</Button>
                                </Link>
                                <Link to="/signup-creator" onClick={closeMobileMenu} className="block">
                                    <Button className="w-full bg-blue-900 hover:bg-blue-800 text-white rounded-full font-semibold">
                                        {t('actions.getStarted')}
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
